import { el } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { openModal } from '../../components/modal.js';
import { toast } from '../../components/toast.js';
import { cxBtn } from './ui.js';

function contextOf(opts, connector) {
  let user = {};
  try { user = JSON.parse(localStorage.getItem('user') || '{}') || {}; } catch { user = {}; }
  return {
    tenant: user.tenant_name || user.tenant_slug || '',
    product: (opts && opts.productName) || '',
    connector: connector || '',
  };
}

function looksLikeCredential(text) {
  return /-----BEGIN |ghp_|github_pat_|xox[baprs]-|sk_live_|sk-[A-Za-z0-9_-]{16,}|AIza[0-9A-Za-z_-]{20,}/.test(String(text || ''));
}

function slotRows(days, pick) {
  if (!days.length) {
    return el('p', { class: 'cx-copy' }, 'No times this week. Message us and we’ll find a slot.');
  }
  return days.map((day) => el('div', { class: 'cx-day' },
    el('p', { class: 'cx-kicker' }, day.label || 'Day'),
    el('div', { class: 'cx-slots' }, (day.slots || []).map((slot) => {
      const taken = !!slot.booked;
      const label = `${day.label || ''} · ${slot.label || ''}`.trim();
      return el('button', {
        type: 'button',
        class: taken ? 'cx-slot is-taken' : 'cx-slot',
        disabled: taken ? true : null,
        onClick: (event) => pick(slot, label, event.currentTarget),
      }, taken ? `${slot.label} · Taken` : (slot.label || 'Time'));
    }))));
}

async function bookSession(opts) {
  const daysRes = await api('/support/slots');
  const days = daysRes && Array.isArray(daysRes.days) ? daysRes.days : [];
  const failed = daysRes && daysRes.error;
  let chosen = null;
  let confirmBtn = null;
  const body = el('div', { class: 'cx-book' },
    el('p', { class: 'cx-copy' }, failed
      ? 'The calendar could not be loaded. Message us and we’ll find a time.'
      : 'Pick a time with the Bonnie team.'),
    failed ? null : slotRows(days, (slot, label, node) => {
      chosen = { iso: slot.iso, label };
      body.querySelectorAll('.cx-slot').forEach((btn) => btn.classList.remove('is-on'));
      node.classList.add('is-on');
      if (confirmBtn) confirmBtn.disabled = false;
    }));
  const modal = openModal({ title: 'Book a session', body, className: 'cx-modal' });
  confirmBtn = cxBtn('Confirm booking', {
    className: 'cx-cta',
    disabled: true,
    onClick: () => sendBooking(opts, chosen, confirmBtn, modal.close),
  });
  body.append(el('div', { class: 'cx-book-foot' },
    cxBtn('Cancel', { onClick: modal.close }),
    confirmBtn));
}

async function sendBooking(opts, chosen, button, close) {
  if (!chosen || !chosen.iso) return;
  button.disabled = true;
  button.textContent = 'Sending…';
  const ctx = contextOf(opts, '');
  const result = await api('/support/booking', {
    method: 'POST',
    body: {
      slot_iso: chosen.iso,
      slot_label: chosen.label,
      tenant: ctx.tenant,
      product: ctx.product,
      connector: ctx.connector,
    },
  });
  if (!result || result.error) {
    button.disabled = false;
    button.textContent = 'Confirm booking';
    toast((result && result.error) || 'Could not book that time', 'error');
    return;
  }
  close();
  toast('Session booked');
  if (opts.onBooked) opts.onBooked(result.session || { slot_label: chosen.label });
}

function messageForm(opts, close) {
  const input = el('textarea', {
    class: 'cx-area',
    rows: '5',
    maxlength: '4000',
    placeholder: 'What are you stuck on? Don’t paste keys or tokens.',
  });
  const error = el('p', { class: 'cx-form-error', role: 'alert' });
  const send = cxBtn('Send', {
    className: 'cx-cta',
    onClick: async () => {
      const text = input.value.trim();
      error.textContent = '';
      if (!text) { error.textContent = 'Write a message first.'; return; }
      if (looksLikeCredential(text)) {
        error.textContent = 'That looks like a credential. It was not sent — paste it on the connector form.';
        return;
      }
      send.disabled = true;
      const ctx = contextOf(opts, '');
      const result = await api('/support/message', {
        method: 'POST',
        body: { message: text, tenant: ctx.tenant, product: ctx.product, connector: ctx.connector },
      });
      send.disabled = false;
      if (!result || result.error) {
        error.textContent = (result && result.error) || 'Could not send the message';
        return;
      }
      close();
      toast('Message sent');
    },
  });
  return el('div', { class: 'cx-book' },
    el('p', { class: 'cx-copy' }, 'Never paste keys in email or chat. Credentials stay on the connect form.'),
    error,
    input,
    el('div', { class: 'cx-book-foot' }, cxBtn('Cancel', { onClick: close }), send));
}

function openMessage(opts) {
  const holder = { close() {} };
  const modal = openModal({
    title: 'Message us',
    className: 'cx-modal',
    body: messageForm(opts, () => holder.close()),
  });
  holder.close = modal.close;
}

export function renderHelp(opts, session) {
  const booked = session && (session.slot_label || session.slot_time);
  return el('section', { class: 'cx-help rv', 'aria-label': 'Help session' },
    el('div', { class: 'cx-help-copy' },
      el('span', { class: 'cx-kicker' }, '§ Help — 20 minutes, once'),
      el('h2', {}, 'Credentials look ', el('em', {}, 'intimidating?')),
      el('p', {}, 'A 20-minute call wires them with you — most teams do this once. Never paste keys in email or chat.'),
      booked ? el('p', { class: 'cx-booked' }, `Booked: ${session.slot_label || session.slot_time}`) : null),
    el('div', { class: 'cx-help-actions' },
      cxBtn(booked ? 'Reschedule' : 'Book a Session', {
        className: 'cx-cta cx-on-signal',
        arrow: true,
        onClick: () => bookSession(opts),
      }),
      el('button', {
        type: 'button',
        class: 'cx-msg',
        onClick: () => openMessage(opts),
      }, 'Message Us')));
}
